// One-shot text-to-speech playback for narrative "Listen" buttons, via the backend
// /Agent/speech endpoint (regional Azure OpenAI TTS). Distinct from useVoiceConversation —
// no recording, no fallback voice: on failure the button just resets.
//
// Synthesized audio is cached per text (as a blob URL, promise-keyed so a hover prefetch
// and a click never double-fetch), so stop→replay and revisiting a voice tab play
// instantly. Deliberately NO server-side precompute — synthesis only happens on user
// intent (hover or click).
import { useCallback, useEffect, useRef, useState } from "react";
import api from "../services/api";

// Backend SpeechRequest caps Text at 1000 chars; narratives are 3-5 sentences so this
// only truncates pathological cases.
const MAX_TTS_LENGTH = 1000;

// Primed muted inside the click gesture so the async play() after the TTS fetch is
// allowed on Safari/iOS (same unlock pattern as voice mode).
const PRIMER_CLIP = "/audio/ack/ack-1.mp3";

// Per-instance cache entries beyond this are evicted oldest-first (a panel has 7 voices).
const MAX_CACHE_ENTRIES = 8;

// Only one narrative plays at a time across all mounted buttons.
let activeStop = null;

export const useTextToSpeech = () => {
  const [status, setStatus] = useState("idle"); // idle | loading | playing
  const audioRef = useRef(null);
  const cacheRef = useRef(new Map()); // trimmed text -> Promise<objectUrl>

  const getAudio = () => {
    if (!audioRef.current) audioRef.current = new Audio();
    return audioRef.current;
  };

  // Returns a promise of a playable object URL; caches in-flight requests so
  // concurrent prefetch + click share one synthesis call.
  const fetchAudioUrl = useCallback((toSpeak) => {
    const cache = cacheRef.current;
    if (cache.has(toSpeak)) return cache.get(toSpeak);

    const promise = api
      .post("/Agent/speech", { text: toSpeak }, { responseType: "blob" })
      .then((response) => URL.createObjectURL(new Blob([response.data], { type: "audio/mpeg" })));
    promise.catch(() => cache.delete(toSpeak)); // failed synthesis isn't cached

    cache.set(toSpeak, promise);
    if (cache.size > MAX_CACHE_ENTRIES) {
      const oldestKey = cache.keys().next().value;
      const oldest = cache.get(oldestKey);
      cache.delete(oldestKey);
      oldest.then((url) => URL.revokeObjectURL(url)).catch(() => {});
    }
    return promise;
  }, []);

  // Start synthesis ahead of the click (e.g. on hover) — no playback, no status change.
  const prefetch = useCallback(
    (text) => {
      const toSpeak = (text || "").trim().slice(0, MAX_TTS_LENGTH);
      if (!toSpeak) return;
      fetchAudioUrl(toSpeak).catch(() => {});
    },
    [fetchAudioUrl]
  );

  const stop = useCallback(() => {
    const audio = audioRef.current;
    if (audio) {
      audio.onended = null;
      audio.onerror = null;
      audio.pause();
    }
    setStatus("idle");
  }, []);

  const play = useCallback(
    async (text) => {
      const toSpeak = (text || "").trim().slice(0, MAX_TTS_LENGTH);
      if (!toSpeak) return;

      if (activeStop && activeStop !== stop) activeStop();
      activeStop = stop;

      const audio = getAudio();
      audio.onended = null;
      audio.onerror = null;
      audio.muted = true;
      audio.src = PRIMER_CLIP;
      audio.play().catch(() => {}); // gesture-initiated unlock; audible only once TTS arrives

      setStatus(cacheRef.current.has(toSpeak) ? "playing" : "loading");
      try {
        const url = await fetchAudioUrl(toSpeak);
        const finish = () => {
          audio.onended = null;
          audio.onerror = null;
          setStatus("idle"); // URL stays cached for instant replay
        };
        audio.onended = finish;
        audio.onerror = finish;
        audio.muted = false;
        audio.src = url;
        setStatus("playing");
        audio.play().catch(finish);
      } catch {
        audio.pause();
        setStatus("idle");
      }
    },
    [stop, fetchAudioUrl]
  );

  useEffect(
    () => () => {
      if (activeStop === stop) activeStop = null;
      stop();
      cacheRef.current.forEach((promise) =>
        promise.then((url) => URL.revokeObjectURL(url)).catch(() => {})
      );
      cacheRef.current.clear();
    },
    [stop]
  );

  return { status, play, stop, prefetch };
};

export default useTextToSpeech;
